"use client";
import { Vehicle } from "@alba-cars/common-modules";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import type { Swiper as SwiperType } from "swiper";
import { Navigation, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import IconArrowDown from "@/components/Icons/IconArrowDown";
import CarsCard from "@/components/ui/CarsCard";
import "swiper/css";
import "swiper/css/navigation";
import { Link } from "@/i18n/routing";

interface TwoTwelveCarsProps {
  cars: Vehicle[];
}

const BrandNew212Swiper = ({ cars }: TwoTwelveCarsProps) => {
  const t = useTranslations("Articles.article.brandNew212Swiper");
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperType | null>(null);

  const handleNextSlide = () => {
    if (!swiperRef.current) return;

    if (activeIndex === cars.length - 1) {
      swiperRef.current.slideTo(0);
    } else {
      swiperRef.current.slideNext();
    }
  };

  if (!cars || cars.length === 0) {
    return null;
  }

  return (
    <div className="relative rounded-2xl bg-white px-4 py-6" id="brand-new-212-swiper">
      <div className="mb-6 flex items-center justify-between px-2">
        <h3 className="urbanist text-xl font-semibold">{t("sectionTitle")}</h3>
        <Link
          href={"/buy-used-cars-uae?make=212&isBrandNew=true"}
          className="inter font-semibold text-primary transition-all duration-300 hover:text-primary-dark hover:underline"
        >
          {t("seeAll")}
        </Link>
      </div>
      <div className="relative">
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          modules={[Navigation, Virtual]}
          navigation={{
            nextEl: ".brand-new-212-swiper-next",
            prevEl: ".brand-new-212-swiper-prev",
          }}
          preventInteractionOnTransition={true}
          virtual={{
            addSlidesAfter: 1,
            addSlidesBefore: 1,
            enabled: true,
          }}
          slidesPerView={1}
          spaceBetween={16}
          direction="vertical"
          className="h-[450px]"
          autoplay
        >
          {cars.map((vehicle: Vehicle) => (
            <SwiperSlide key={vehicle.id}>
              <CarsCard car={vehicle} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="flex justify-end">
          <button
            onClick={handleNextSlide}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleNextSlide();
              }
            }}
            className="flex w-full items-center justify-center bg-white text-center text-white transition-all duration-300"
            aria-label="Next slide"
            tabIndex={0}
          >
            <span className="flex animate-bounce items-center gap-2 text-primary">
              <IconArrowDown fill="#ff3347" />
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandNew212Swiper;
